import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "utils/cn";

const badgeVariants = cva(
  "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border font-medium",
  {
    variants: {
      tone: {
        neutral: "border-border bg-surface-muted text-muted-foreground",
        primary: "border-primary/20 bg-primary-subtle text-primary-text",
        info: "border-info/20 bg-info-subtle text-info-text",
        success: "border-success/20 bg-success-subtle text-success-text",
        warning: "border-warning/20 bg-warning-subtle text-warning-text",
        danger: "border-danger/20 bg-danger-subtle text-danger-text",
      },
      size: {
        sm: "px-2 py-0.5 text-xs",
        md: "px-2.5 py-1 text-xs",
      },
    },
    defaultVariants: { tone: "neutral", size: "sm" },
  }
);

const dotTone: Record<NonNullable<BadgeProps["tone"]>, string> = {
  neutral: "bg-subtle-foreground",
  primary: "bg-primary",
  info: "bg-info",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger",
};

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  /** Leading status dot in the tone color. */
  dot?: boolean;
}

export const Badge: React.FC<BadgeProps> = ({ tone, size, dot, className, children, ...props }) => (
  <span data-ui="badge" className={cn(badgeVariants({ tone, size }), className)} {...props}>
    {dot && (
      <span
        aria-hidden="true"
        className={cn("h-1.5 w-1.5 rounded-full", dotTone[tone || "neutral"])}
      />
    )}
    {children}
  </span>
);
